import { Component, DestroyRef, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { interval } from 'rxjs';
import { ImpersonationActive } from 'src/app/auth/models/impersonation';
import { ImpersonationService } from 'src/app/auth/services/impersonation.service';
import { ImpersonationDialogComponent } from 'src/app/operational/components/impersonation-dialog/impersonation-dialog.component';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  template: `
    @if (session(); as s) {
      <div class="banner" role="status">
        <span class="text">A ver como: {{ label(s) }}</span>
        <span class="time">Tempo restante: {{ timeLeft() }}</span>
        <button type="button" class="btn" (click)="change()">Mudar</button>
        <button type="button" class="btn" [disabled]="ending()" (click)="end()">Terminar</button>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        flex: none;
      }
      .banner {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        padding: 8px 16px;
        background: var(--op-warning, #f59e0b);
        color: #0f172a;
        font-size: 14px;
        font-weight: 600;
      }
      .text {
        flex: 1;
      }
      .time {
        font-variant-numeric: tabular-nums;
      }
      .btn {
        height: 32px;
        padding: 0 14px;
        border: 1px solid #0f172a;
        border-radius: 6px;
        background: transparent;
        color: #0f172a;
        font-weight: 600;
        cursor: pointer;
      }
      .btn:disabled {
        opacity: 0.65;
        cursor: default;
      }
    `,
  ],
})
export class ImpersonationBannerComponent implements OnInit {
  private readonly service = inject(ImpersonationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly session = signal<ImpersonationActive | null>(null);
  readonly timeLeft = signal('');
  readonly ending = signal(false);

  private lastCheck = 0;

  ngOnInit(): void {
    this.service.active$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((s) => {
      this.session.set(s);
      this.updateTime();
    });

    // Restore the banner after a page load.
    this.service.load().subscribe({ error: () => undefined });

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateTime());
  }

  label(s: ImpersonationActive): string {
    if (s.kind === 'lab') {
      const contact = s.contact?.nome || s.contact?.email || '';
      const lab = s.lab?.nome ?? '';
      return `${contact} (${lab})`;
    }
    const level = s.accessLevel != null ? ` · nível ${s.accessLevel}` : '';
    return s.pharmacies.map((p) => p.nome).join(', ') + level;
  }

  /** Opens the same dialog; starting a new session replaces the current one. */
  change(): void {
    this.dialog.open(ImpersonationDialogComponent, { width: '720px', maxWidth: '95vw', panelClass: 'op-dark-dialog' });
  }

  end(): void {
    this.ending.set(true);
    this.service.end().subscribe({
      next: () => this.service.reload(),
      error: () => this.ending.set(false),
    });
  }

  private updateTime(): void {
    const s = this.session();
    if (!s) {
      this.timeLeft.set('');
      return;
    }
    const ms = new Date(s.expiresAt).getTime() - Date.now();
    if (ms > 0) {
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.timeLeft.set(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      return;
    }

    this.timeLeft.set('0:00');
    // Expired: ask the API (at most every 5 seconds). If it says inactive, show the operator's own view.
    if (Date.now() - this.lastCheck < 5000) {
      return;
    }
    this.lastCheck = Date.now();
    this.service.load().subscribe({
      next: (state) => {
        if (!state.active) {
          this.service.reload();
        }
      },
      error: () => undefined,
    });
  }
}
